// Mapeamento bidirecional entre FHIR AllergyIntolerance e sistema interno
import { 
  FHIRAllergyIntolerance,
  FHIRAllergyIntoleranceClinicalStatus,
  FHIRAllergyIntoleranceVerificationStatus,
  FHIRAllergyIntoleranceType,
  FHIRAllergyIntoleranceCategory,
  FHIRAllergyIntoleranceCriticality,
  FHIRAllergyIntoleranceSeverity,
  FHIRAllergyIntoleranceReaction,
  FHIRCodeableConcept,
  FHIRReference,
  AllergyIntoleranceMapping,
  COMMON_ALLERGY_CODES,
  COMMON_MANIFESTATIONS
} from '../types/fhir-allergy-intolerance'
import { EnhancedMedicalRecord } from './medicalRecordsSystem'

// Utilitários para conversão de prioridade/criticidade
const mapCriticalityToPriority = (criticality?: FHIRAllergyIntoleranceCriticality): 'low' | 'normal' | 'high' | 'critical' => {
  switch (criticality) {
    case FHIRAllergyIntoleranceCriticality.HIGH:
      return 'critical'
    case FHIRAllergyIntoleranceCriticality.LOW:
      return 'low'
    case FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS:
      return 'normal'
    default:
      return 'normal'
  }
}

const mapPriorityToCriticality = (priority: string): FHIRAllergyIntoleranceCriticality => {
  switch (priority) {
    case 'critical':
    case 'high':
      return FHIRAllergyIntoleranceCriticality.HIGH
    case 'low':
      return FHIRAllergyIntoleranceCriticality.LOW
    default:
      return FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS
  }
}

// Utilitários para CodeableConcept
const createCodeableConcept = (code?: string, display?: string, system?: string): FHIRCodeableConcept => {
  if (!code && !display) return {}
  
  return {
    coding: code ? [{
      system: system || 'http://snomed.info/sct',
      code,
      display: display || code
    }] : undefined,
    text: display || code
  }
}

const extractCodeFromCodeableConcept = (concept?: FHIRCodeableConcept): { code?: string, display?: string } => {
  if (!concept) return {}
  
  const coding = concept.coding?.[0]
  return {
    code: coding?.code,
    display: coding?.display || concept.text
  }
}

// Utilitários para Reference
const createPatientReference = (patientId: string): FHIRReference => ({
  reference: `Patient/${patientId}`,
  type: 'Patient'
})

const createPractitionerReference = (doctorId: string): FHIRReference => ({
  reference: `Practitioner/${doctorId}`,
  type: 'Practitioner'
})

// Converter do sistema interno para FHIR
export const convertToFHIR = (record: EnhancedMedicalRecord): FHIRAllergyIntolerance => {
  const customFields = record.custom_fields || {}
  
  const fhirRecord: FHIRAllergyIntolerance = {
    resourceType: 'AllergyIntolerance',
    id: record.id,
    patient: createPatientReference(record.patient_id),
    
    // Status clínico
    clinicalStatus: customFields.clinical_status ? 
      createCodeableConcept(
        customFields.clinical_status,
        customFields.clinical_status,
        'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical'
      ) : undefined,
    
    // Status de verificação
    verificationStatus: customFields.verification_status ?
      createCodeableConcept(
        customFields.verification_status,
        customFields.verification_status,
        'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification'
      ) : undefined,
    
    // Tipo
    type: customFields.type as FHIRAllergyIntoleranceType,
    
    // Categoria
    category: customFields.category as FHIRAllergyIntoleranceCategory[],
    
    // Criticidade
    criticality: mapPriorityToCriticality(record.priority),
    
    // Substância/código
    code: createCodeableConcept(
      customFields.substance_code,
      customFields.substance_display || record.title
    ),
    
    // Datas
    onsetDateTime: customFields.onset,
    recordedDate: customFields.recorded_date || record.created_at,
    
    // Asserter (médico que registrou)
    asserter: createPractitionerReference(record.doctor_id),
    
    // Reações
    reaction: customFields.reactions?.map((reaction: any): FHIRAllergyIntoleranceReaction => ({
      manifestation: reaction.manifestation_codes?.map((code: string, index: number) => 
        createCodeableConcept(
          code,
          reaction.manifestation_displays?.[index],
          'http://snomed.info/sct'
        )
      ) || [],
      severity: reaction.severity as FHIRAllergyIntoleranceSeverity,
      description: reaction.description,
      onset: reaction.onset
    })),
    
    // Notas
    note: customFields.notes?.map((noteText: string) => ({
      text: noteText,
      time: record.created_at
    }))
  }
  
  // Remover campos undefined
  return JSON.parse(JSON.stringify(fhirRecord))
}

// Converter de FHIR para sistema interno
export const convertFromFHIR = (
  fhirRecord: FHIRAllergyIntolerance,
  doctorId: string,
  recordId?: string
): Partial<EnhancedMedicalRecord> => {
  const patientId = fhirRecord.patient.reference?.replace('Patient/', '') || ''
  
  // Extrair códigos e displays
  const substanceInfo = extractCodeFromCodeableConcept(fhirRecord.code)
  const clinicalStatusInfo = extractCodeFromCodeableConcept(fhirRecord.clinicalStatus)
  const verificationStatusInfo = extractCodeFromCodeableConcept(fhirRecord.verificationStatus)
  
  // Converter reações
  const reactions = fhirRecord.reaction?.map(reaction => {
    const manifestations = reaction.manifestation.map(manifestation => 
      extractCodeFromCodeableConcept(manifestation)
    )
    
    return {
      manifestation_codes: manifestations.map(m => m.code).filter(Boolean),
      manifestation_displays: manifestations.map(m => m.display).filter(Boolean),
      severity: reaction.severity,
      description: reaction.description,
      onset: reaction.onset
    }
  }) || []
  
  // Converter notas
  const notes = fhirRecord.note?.map(note => note.text) || []
  
  return {
    id: recordId || fhirRecord.id,
    patient_id: patientId,
    doctor_id: doctorId,
    title: substanceInfo.display || 'Alergia/Intolerância',
    description: fhirRecord.note?.[0]?.text || `Alergia/intolerância a ${substanceInfo.display}`,
    record_type: 'allergy_intolerance',
    priority: mapCriticalityToPriority(fhirRecord.criticality),
    status: 'active',
    created_at: fhirRecord.recordedDate || new Date().toISOString(),
    custom_fields: {
      clinical_status: clinicalStatusInfo.code as FHIRAllergyIntoleranceClinicalStatus,
      verification_status: verificationStatusInfo.code as FHIRAllergyIntoleranceVerificationStatus,
      type: fhirRecord.type,
      category: fhirRecord.category,
      criticality: fhirRecord.criticality,
      substance_code: substanceInfo.code,
      substance_display: substanceInfo.display,
      onset: fhirRecord.onsetDateTime,
      recorded_date: fhirRecord.recordedDate,
      reactions,
      notes
    }
  }
}

// Validar compatibilidade FHIR
export const validateFHIRRecord = (fhirRecord: FHIRAllergyIntolerance): { isValid: boolean, errors: string[] } => {
  const errors: string[] = []
  
  // Validações obrigatórias FHIR
  if (fhirRecord.resourceType !== 'AllergyIntolerance') {
    errors.push('resourceType deve ser "AllergyIntolerance"')
  }
  
  if (!fhirRecord.patient?.reference) {
    errors.push('Referência do paciente é obrigatória')
  }
  
  // Validações de enum
  if (fhirRecord.type && !Object.values(FHIRAllergyIntoleranceType).includes(fhirRecord.type)) {
    errors.push(`Tipo inválido: ${fhirRecord.type}`)
  }
  
  if (fhirRecord.criticality && !Object.values(FHIRAllergyIntoleranceCriticality).includes(fhirRecord.criticality)) {
    errors.push(`Criticidade inválida: ${fhirRecord.criticality}`)
  }
  
  if (fhirRecord.category) {
    for (const cat of fhirRecord.category) {
      if (!Object.values(FHIRAllergyIntoleranceCategory).includes(cat)) {
        errors.push(`Categoria inválida: ${cat}`)
      }
    }
  }
  
  // Validar reações
  if (fhirRecord.reaction) {
    for (const reaction of fhirRecord.reaction) {
      if (!reaction.manifestation || reaction.manifestation.length === 0) {
        errors.push('Reação deve ter pelo menos uma manifestação')
      }
      
      if (reaction.severity && !Object.values(FHIRAllergyIntoleranceSeverity).includes(reaction.severity)) {
        errors.push(`Severidade de reação inválida: ${reaction.severity}`)
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

// Criar um novo registro FHIR com valores padrão
export const createDefaultFHIRRecord = (patientId: string, doctorId: string): FHIRAllergyIntolerance => {
  return {
    resourceType: 'AllergyIntolerance',
    id: `allergy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    patient: createPatientReference(patientId),
    clinicalStatus: createCodeableConcept(
      'active',
      'Active',
      'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical'
    ),
    verificationStatus: createCodeableConcept(
      'unconfirmed',
      'Unconfirmed',
      'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification'
    ),
    type: FHIRAllergyIntoleranceType.ALLERGY,
    category: [FHIRAllergyIntoleranceCategory.FOOD],
    criticality: FHIRAllergyIntoleranceCriticality.UNABLE_TO_ASSESS,
    recordedDate: new Date().toISOString(),
    asserter: createPractitionerReference(doctorId)
  }
}

// Utilitários para busca de códigos comuns
export const findCommonAllergyCode = (searchTerm: string) => {
  const term = searchTerm.toLowerCase()
  return Object.entries(COMMON_ALLERGY_CODES).find(([key, value]) => 
    key.toLowerCase().includes(term) || 
    value.display.toLowerCase().includes(term)
  )?.[1]
}

export const findCommonManifestation = (searchTerm: string) => {
  const term = searchTerm.toLowerCase()
  return Object.entries(COMMON_MANIFESTATIONS).find(([key, value]) => 
    key.toLowerCase().includes(term) || 
    value.display.toLowerCase().includes(term)
  )?.[1]
}

// Exportar mapeamento completo
export const createAllergyIntoleranceMapping = (
  fhirRecord: FHIRAllergyIntolerance,
  internalRecord: EnhancedMedicalRecord
): AllergyIntoleranceMapping => ({
  fhir: fhirRecord,
  internal: convertFromFHIR(fhirRecord, internalRecord.doctor_id, internalRecord.id)
})